export const NURSING_PROGRAM_OPTIONS = [
  { label: "Chronic Care Management (CCM)", value: "ccm" },
  { label: "Principal Care Management (PCM)", value: "pcm" },
  { label: "Remote Patient Monitoring (RPM)", value: "rpm" },
  { label: "Advanced Primary Care Management (APCM)", value: "apcm" },
] as const;

export function formatNursingProgramLabel(program: unknown): string {
  const key = String(program ?? "").trim().toLowerCase();
  if (!key) return "";
  const match = NURSING_PROGRAM_OPTIONS.find((option) => option.value === key);
  return match?.label ?? key.toUpperCase();
}

export function formatNursingProgramTitle(program: unknown): string {
  const label = formatNursingProgramLabel(program);
  if (!label) return "";
  return label.replace(/\s*\([A-Za-z]+\)\s*$/, "").trim();
}

export function formatNursingProgramAcronym(program: unknown): string {
  const key = String(program ?? "").trim().toLowerCase();
  return key ? key.toUpperCase() : "";
}

export const NURSING_CARE_TYPE_OPTIONS = [
  { label: "Care Coordination", value: "care coordination" },
  { label: "Consulting Provider", value: "consulting provider" },
  { label: "Planning", value: "planning" },
  { label: "Taking Insights", value: "taking insights" },
  { label: "Medical Review", value: "medical review" },
  { label: "Care Planning", value: "care planning" },
  { label: "Discussing POC with Provider", value: "discussing poc with provider" },
  { label: "Communicating POC to Medical Team", value: "communicating poc to medical team" },
  { label: "Discussing POC with Patient", value: "discussing poc with patient" },
  { label: "Discussing Expected Outcomes with Patient", value: "discussing expected outcomes with patient" },
  { label: "Scheduling Appointment", value: "scheduling appointment" },
  { label: "Ordering Labs", value: "ordering labs" },
  { label: "Ordering Referral", value: "ordering referral" },
  { label: "Ordering Imaging/Scan", value: "ordering imaging/scan" },
  { label: "Medication Review", value: "medication review" },
  { label: "Calling Pharmacy", value: "calling pharmacy" },
  { label: "Care Coordination Activity", value: "care coordination activity" },
  { label: "Reviewing EHR", value: "reviewing ehr" },
  { label: "Care Management Services", value: "care management services" },
  { label: "Locating Community Resources", value: "locating community resources" },
  { label: "Portal Messaging Regarding Chronic Conditions", value: "portal messaging regarding chronic conditions" },
] as const;

export function formatNursingCareTypeLabel(careType: unknown): string {
  const values = Array.isArray(careType)
    ? careType
    : String(careType ?? "")
        .split(",")
        .map((part) => part.trim())
        .filter(Boolean);

  if (!values.length) return "";

  return values
    .map((value) => {
      const key = String(value).trim().toLowerCase();
      const match = NURSING_CARE_TYPE_OPTIONS.find((option) => option.value === key);
      return match?.label ?? String(value);
    })
    .filter(Boolean)
    .join(", ");
}

export const NURSING_NOTE_TYPE_OPTIONS = [
  { label: "Onboarding", value: "onboarding" },
  { label: "Training", value: "training" },
  { label: "Triage", value: "triage" },
  { label: "Routine", value: "routine" },
  { label: "Other", value: "other" },
] as const;

export type NursingNoteFormState = {
  program: string;
  careType: string[];
  dateTime: string;
  duration: string;
  notes: string;
  note_type: string;
};

export function createDefaultNursingNoteForm(patientTimeZone?: string): NursingNoteFormState {
  return {
    program: "",
    careType: [],
    dateTime: getLocalDateTimeValue(patientTimeZone),
    duration: "",
    notes: "",
    note_type: "",
  };
}

function formatYmdHmInTimeZone(date: Date, timeZone: string) {
  const dtf = new Intl.DateTimeFormat("sv-SE", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  const parts = dtf.formatToParts(date);
  const get = (type: string) => parts.find((p) => p.type === type)?.value;
  const y = get("year");
  const m = get("month");
  const d = get("day");
  const h = get("hour");
  const min = get("minute");
  if (!y || !m || !d || !h || !min) return { ymd: "", hm: "" };
  return { ymd: `${y}-${m}-${d}`, hm: `${h}:${min}` };
}

export function getLocalDateTimeValue(timeZone?: string): string {
  if (typeof timeZone === "string" && timeZone.trim()) {
    try {
      const { ymd, hm } = formatYmdHmInTimeZone(new Date(), timeZone);
      if (ymd && hm) return `${ymd}T${hm}`;
    } catch {
      // fall back to browser local time
    }
  }
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  const hours = String(now.getHours()).padStart(2, "0");
  const minutes = String(now.getMinutes()).padStart(2, "0");
  return `${year}-${month}-${day}T${hours}:${minutes}`;
}
